import { alpha, createTheme } from '@mui/material/styles';

// Shared component styling, with distinct canvas and panel surfaces in each mode.
export const themeOptions: NonNullable<Parameters<typeof createTheme>[0]> = {
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 14,
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.035em' },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.03em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.025em' },
    h4: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontSize: '1rem', fontWeight: 600 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#2563eb', dark: '#1d4ed8', light: '#60a5fa', contrastText: '#ffffff' },
        background: { default: '#f4f6f9', paper: '#ffffff' },
        text: { primary: '#1e293b', secondary: '#64748b' },
        divider: '#e2e8f0',
        action: { active: '#64748b', hover: 'rgba(15, 23, 42, 0.04)', selected: 'rgba(37, 99, 235, 0.08)', focus: 'rgba(37, 99, 235, 0.12)' },
      },
    },
    dark: {
      palette: {
        primary: { main: '#93b4ff', dark: '#729bfa', light: '#b8ceff', contrastText: '#101827' },
        background: { default: '#101722', paper: '#192332' },
        text: { primary: '#e6edf7', secondary: '#a0aec0' },
        divider: '#2b384b',
        action: { active: '#a0aec0', hover: 'rgba(226, 237, 247, 0.05)', selected: 'rgba(147, 180, 255, 0.12)', focus: 'rgba(147, 180, 255, 0.18)' },
      },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          '--surface-canvas': theme.palette.background.default,
          '--surface-panel': theme.palette.background.paper,
          '--border-color': theme.palette.divider,
          '--primary-color': theme.palette.primary.main,
          '--primary-color-dark': theme.palette.primary.dark,
          '--color-primary-500': theme.palette.primary.main,
          '--color-primary-600': theme.palette.primary.dark,
          '--text-color': theme.palette.text.primary,
          '--text-color-secondary': theme.palette.text.secondary,
          '--shadow-focus-primary': `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
        },
        '::selection': { backgroundColor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.text.primary },
      }),
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ theme }) => ({ borderRadius: 8, padding: '8px 16px', transition: 'background-color 150ms ease, border-color 150ms ease', '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 3 } }),
        sizeSmall: { padding: '6px 12px', fontSize: '0.8125rem' },
        outlined: ({ theme }) => ({ borderColor: alpha(theme.palette.primary.main, 0.3) }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({ borderRadius: 8, '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          fontSize: '0.875rem',
          transition: 'box-shadow 150ms ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.secondary },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: ({ theme }) => ({ boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}, 0 0 0 1px ${theme.palette.divider}` }),
        outlined: ({ theme }) => ({ borderColor: theme.palette.divider }),
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 12 } } },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600 } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', gap: 8 } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 8, fontSize: '0.8125rem' } } },
    MuiTablePagination: { styleOverrides: { toolbar: { flexWrap: 'wrap', justifyContent: 'flex-end', paddingLeft: 8 }, selectLabel: { fontSize: '0.75rem' }, displayedRows: { fontSize: '0.75rem' } } },
    MuiMenu: {
      styleOverrides: { paper: ({ theme }) => ({ marginTop: 4, border: `1px solid ${theme.palette.divider}`, boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.16)}` }) },
    },
    MuiMenuItem: { styleOverrides: { root: { fontSize: '0.8125rem', minHeight: 36 } } },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({ borderColor: theme.palette.divider, padding: '12px 16px', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }),
        head: ({ theme }) => ({ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem' }),
        sizeSmall: { padding: '8px 12px' },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' } } },
    MuiTooltip: { styleOverrides: { tooltip: { fontSize: '0.75rem', lineHeight: 1.5, borderRadius: 6 } } },
    MuiTabs: { styleOverrides: { root: { minHeight: 40 }, indicator: { height: 2, borderRadius: 2 } } },
    MuiTab: { styleOverrides: { root: { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' } } },
  },
};
